import { useState, useEffect, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import SearchFilters from "@/components/SearchFilters";
import SagraCard from "@/components/SagraCard";
import { Button } from "@/components/ui/button";
import { MapPin, Map, LayoutGrid, Loader2, Calendar } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lazy load heavy components
const SagreMap = lazy(() => import("@/components/SagreMap").catch(() => ({ default: () => <div className="h-[500px] bg-muted/50 rounded-xl flex items-center justify-center text-muted-foreground">Mappa non disponibile</div> })));
const SagreCalendar = lazy(() => import("@/components/SagreCalendar"));

const MapLoader = () => (
  <div className="h-[500px] bg-muted/50 rounded-xl flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const CalendarLoader = () => (
  <div className="h-[500px] bg-muted/50 rounded-xl flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const [sagre, setSagre] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    const regione = searchParams.get("regione");
    const mese = searchParams.get("mese");
    const geo = searchParams.get("geo");

    if (geo === "true") {
      handleGeolocationSearch();
    } else {
      handleSearch({ regione: regione || "", mese: mese || "" });
    }
  }, []);

  const handleGeolocationSearch = () => {
    if (!navigator.geolocation) return;

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(loc);
        handleSearch({
          useGeolocation: true,
          lat: loc.lat,
          lng: loc.lng,
          raggio_km: "100"
        });
      },
      () => {
        setLoading(false);
        handleSearch({});
      }
    );
  };

  const handleSearch = async (filters) => {
    setLoading(true);
    setSearched(true);

    try {
      const params = new URLSearchParams();
      
      if (filters.regione) params.append("regione", filters.regione);
      if (filters.provincia) params.append("provincia", filters.provincia);
      if (filters.mese) params.append("mese", filters.mese);
      
      if (filters.useGeolocation && filters.lat && filters.lng) {
        params.append("lat", filters.lat);
        params.append("lng", filters.lng);
        params.append("raggio_km", filters.raggio_km || "100");
        setUserLocation({ lat: filters.lat, lng: filters.lng });
      }

      const response = await axios.get(`${API}/sagre?${params.toString()}`);
      
      // Filtra le sagre concluse (data_fine nel passato)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Imposta a mezzanotte per confronto corretto
      
      const sagreAttive = response.data.filter(sagra => {
        if (!sagra.data_fine) return true; // Se non c'è data_fine, mostra la sagra
        const dataFine = new Date(sagra.data_fine);
        return dataFine >= today; // Mostra solo se la data di fine è oggi o nel futuro
      });
      
      setSagre(sagreAttive);
    } catch (error) {
      console.error("Error searching sagre:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="search-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl text-foreground mb-1">
                Esplora le sagre
              </h1>
              <p className="text-muted-foreground text-sm">
                {searched && !loading && (
                  sagre.length === 0 
                    ? "Nessun risultato" 
                    : `${sagre.length} sagr${sagre.length === 1 ? 'a' : 'e'} trovat${sagre.length === 1 ? 'a' : 'e'}`
                )}
              </p>
            </div>
            
            {/* View Toggle */}
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="rounded-md h-8 px-3"
                data-testid="view-grid-btn"
                title="Vista griglia"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "calendar" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("calendar")}
                className="rounded-md h-8 px-3"
                data-testid="view-calendar-btn"
                title="Vista calendario"
              >
                <Calendar className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "map" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("map")}
                className="rounded-md h-8 px-3"
                data-testid="view-map-btn"
                title="Vista mappa"
              >
                <Map className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <SearchFilters 
            onSearch={handleSearch}
            initialFilters={{
              regione: searchParams.get("regione") || "",
              mese: searchParams.get("mese") || ""
            }}
          />
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Loading */}
        {loading && (
          viewMode === "map" ? (
            <div className="h-[500px] bg-muted rounded-xl skeleton-gentle" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="skeleton-gentle">
                  <div className="aspect-[3/2] bg-muted rounded-xl mb-3" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          )
        )}

        {/* Map View */}
        {!loading && viewMode === "map" && sagre.length > 0 && (
          <div className="h-[600px]">
            <Suspense fallback={<MapLoader />}>
              <SagreMap 
                sagre={sagre} 
                userLocation={userLocation}
                className="h-full"
              />
            </Suspense>
          </div>
        )}

        {/* Calendar View */}
        {!loading && viewMode === "calendar" && (
          <div className="max-w-3xl mx-auto">
            <Suspense fallback={<CalendarLoader />}>
              <SagreCalendar sagre={sagre} />
            </Suspense>
          </div>
        )}

        {/* Grid View */}
        {!loading && viewMode === "grid" && sagre.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="sagre-grid">
            {sagre.map((sagra, index) => (
              <SagraCard key={sagra.id} sagra={sagra} index={index} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && searched && sagre.length === 0 && (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-5 h-5 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              Nessuna sagra trovata
            </h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Prova a modificare i filtri o a cercare in un'altra zona.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;